import { useStore } from '@store/index';
import { Copy } from 'lucide-react';
import React from 'react';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useCloneCase } from '@/hooks/useCases';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * "Clone Case" entry for a case's actions menu (case table row menu and the
 * case details "More options" menu).
 *
 * Shown only for organizations that opt in via the dynamic config key
 * `feature_flag/clone_case_<org_id>` = true (BV-2234). An absent or false key,
 * or a flag that is still loading or failed to load, hides it. This only hides
 * the option; the clone API itself is not restricted.
 */
export const CloneCaseMenuItem: React.FC<{
  caseId: string;
  /** The case's organization; falls back to the selected organization. */
  organizationId?: string;
  iconClassName?: string;
}> = ({ caseId, organizationId, iconClassName = 'mr-2 h-4 w-4' }) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const orgId = organizationId || selectedOrganizationId;
  const isCloneCaseEnabled = useFeatureFlag(orgId ? `clone_case_${orgId}` : undefined);
  const cloneCase = useCloneCase();

  if (!isCloneCaseEnabled) {
    return null;
  }

  return (
    <DropdownMenuItem
      onClick={e => {
        e.stopPropagation();
        cloneCase.mutate({ caseId });
      }}
      className="cursor-pointer"
      disabled={cloneCase.isPending}
    >
      <Copy className={iconClassName} />
      {cloneCase.isPending ? 'Cloning...' : 'Clone Case'}
    </DropdownMenuItem>
  );
};
